// Cookie management utilities
export const COOKIE_TYPES = {
  NECESSARY: 'necessary',
  FUNCTIONAL: 'functional',
  ANALYTICS: 'analytics',
  MARKETING: 'marketing'
};

export const COOKIE_NAMES = {
  CONSENT: 'cookie_consent',
  SESSION: 'session_id',
  PREFERENCES: 'user_preferences',
  ANALYTICS: 'analytics_id',
  MARKETING: 'marketing_id'
};

// Get cookie consent status
export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem(COOKIE_NAMES.CONSENT);
    return consent ? JSON.parse(consent) : null;
  } catch (error) {
    console.error('Error reading cookie consent:', error);
    return null;
  }
};

// Check if specific cookie type is allowed
export const isCookieAllowed = (type) => {
  const consent = getCookieConsent();
  if (!consent) return false;
  
  // Necessary cookies are always allowed
  if (type === COOKIE_TYPES.NECESSARY) return true;
  
  return consent[type] === true;
};

// Set a cookie with consent check
export const setCookie = (name, value, days = 365, type = COOKIE_TYPES.NECESSARY) => {
  if (!isCookieAllowed(type)) {
    console.log(`Cookie ${name} not set - ${type} cookies not allowed`);
    return false;
  }
  
  try {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict;Secure`;
    return true;
  } catch (error) {
    console.error('Error setting cookie:', error);
    return false;
  }
};

// Get cookie value
export const getCookie = (name) => {
  try {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  } catch (error) {
    console.error('Error getting cookie:', error);
    return null;
  }
};

// Delete a cookie
export const deleteCookie = (name) => {
  try {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
    return true;
  } catch (error) {
    console.error('Error deleting cookie:', error);
    return false;
  }
};

// Clear all cookies of a specific type
export const clearCookiesByType = (type) => {
  if (type === COOKIE_TYPES.NECESSARY) {
    console.warn('Cannot clear necessary cookies');
    return;
  }

  const cookiesToClear = {
    [COOKIE_TYPES.ANALYTICS]: ['_ga', '_ga_*', '_gid', '_gat', 'analytics_id'],
    [COOKIE_TYPES.MARKETING]: ['_fbp', '_fbc', 'marketing_id', 'utm_*'],
    [COOKIE_TYPES.FUNCTIONAL]: ['user_preferences', 'theme', 'language']
  };

  const cookies = cookiesToClear[type] || [];
  
  cookies.forEach(cookie => {
    if (cookie.includes('*')) {
      // Handle wildcard cookies
      const prefix = cookie.replace('*', '');
      document.cookie.split(';').forEach(c => {
        const name = c.split('=')[0].trim();
        if (name.startsWith(prefix)) {
          deleteCookie(name);
        }
      });
    } else {
      deleteCookie(cookie);
    }
  });
  
  console.log(`Cleared ${type} cookies:`, cookies);
};

// Initialize analytics (Google Analytics, etc.)
export const initializeAnalytics = () => {
  if (!isCookieAllowed(COOKIE_TYPES.ANALYTICS)) {
    console.log('Analytics cookies not allowed, skipping initialization');
    return;
  }
  
  console.log('🔍 Analytics cookies enabled - initializing tracking');
  
  // Example Google Analytics initialization
  // Uncomment and configure for actual Google Analytics
  /*
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'GA_MEASUREMENT_ID', {
    anonymize_ip: true,
    cookie_flags: 'SameSite=Strict;Secure'
  });
  */
  
  // Set analytics cookie
  setCookie(COOKIE_NAMES.ANALYTICS, 'enabled', 365, COOKIE_TYPES.ANALYTICS);
};

// Initialize marketing tools
export const initializeMarketing = () => {
  if (!isCookieAllowed(COOKIE_TYPES.MARKETING)) {
    console.log('Marketing cookies not allowed, skipping initialization');
    return;
  }
  
  console.log('📢 Marketing cookies enabled - initializing tracking');
  
  // Example Facebook Pixel initialization
  // Uncomment and configure for actual Facebook Pixel
  /*
  !function(f,b,e,v,n,t,s) {
    if(f.fbq)return;n=f.fbq=function(){n.callMethod? 
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)
  }(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', 'YOUR_PIXEL_ID');
  fbq('track', 'PageView');
  */
  
  // Set marketing cookie
  setCookie(COOKIE_NAMES.MARKETING, 'enabled', 365, COOKIE_TYPES.MARKETING);
};

// Initialize functional features
export const initializeFunctional = () => {
  if (!isCookieAllowed(COOKIE_TYPES.FUNCTIONAL)) {
    console.log('Functional cookies not allowed, skipping initialization');
    return;
  }
  
  console.log('⚙️ Functional cookies enabled - initializing features');
  
  // Load user preferences
  const preferences = getCookie(COOKIE_NAMES.PREFERENCES);
  if (preferences) {
    try {
      const parsed = JSON.parse(preferences);
      console.log('User preferences loaded:', parsed);
      
      // Apply user preferences (theme, language, etc.)
      if (parsed.theme) {
        document.documentElement.classList.toggle('dark', parsed.theme === 'dark');
      }
      
      if (parsed.language) {
        document.documentElement.lang = parsed.language;
      }
    } catch (error) {
      console.error('Error parsing user preferences:', error);
    }
  }
  
  // Set functional cookie
  setCookie(COOKIE_NAMES.FUNCTIONAL, 'enabled', 365, COOKIE_TYPES.FUNCTIONAL);
};

// Update cookie consent and apply changes
export const updateCookieConsent = (newConsent) => {
  console.log('Updating cookie consent:', newConsent);
  
  const currentConsent = getCookieConsent();
  
  // Clear cookies for disabled categories
  Object.keys(COOKIE_TYPES).forEach(key => {
    const type = COOKIE_TYPES[key];
    if (type === COOKIE_TYPES.NECESSARY) return;
    
    const wasEnabled = currentConsent && currentConsent[type];
    const isEnabled = newConsent[type];
    
    if (wasEnabled && !isEnabled) {
      console.log(`Disabling ${type} cookies`);
      clearCookiesByType(type);
    }
  });
  
  // Save new consent
  const consentData = {
    ...newConsent,
    timestamp: new Date().toISOString(),
    version: '1.0'
  };
  
  localStorage.setItem(COOKIE_NAMES.CONSENT, JSON.stringify(consentData));
  console.log('Cookie consent saved:', consentData);
  
  // Initialize enabled services
  if (newConsent.analytics) {
    initializeAnalytics();
  }
  
  if (newConsent.marketing) {
    initializeMarketing();
  }
  
  if (newConsent.functional) {
    initializeFunctional();
  }
  
  // Set necessary cookies (always enabled)
  setCookie(COOKIE_NAMES.SESSION, 'active', 1, COOKIE_TYPES.NECESSARY);
  
  // Dispatch custom event for other parts of the app to listen to
  window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
    detail: newConsent 
  }));
};

// Get cookie consent summary for display
export const getCookieConsentSummary = () => {
  const consent = getCookieConsent();
  if (!consent) return null;
  
  const enabledCount = Object.values(consent).filter(Boolean).length;
  const totalCount = Object.keys(COOKIE_TYPES).length;
  
  return {
    enabled: enabledCount,
    total: totalCount,
    timestamp: consent.timestamp,
    details: consent
  };
};

// Check if consent is expired (optional - for re-consent)
export const isCookieConsentExpired = (days = 365) => {
  const consent = getCookieConsent();
  if (!consent || !consent.timestamp) return true;
  
  const consentDate = new Date(consent.timestamp);
  const expiryDate = new Date(consentDate.getTime() + (days * 24 * 60 * 60 * 1000));
  
  return new Date() > expiryDate;
};

// Initialize necessary cookies (always allowed)
export const initializeNecessaryCookies = () => {
  console.log('🔒 Initializing necessary cookies');
  
  // Session management
  setCookie(COOKIE_NAMES.SESSION, 'active', 1, COOKIE_TYPES.NECESSARY);
  
  // Security tokens, CSRF protection, etc.
  // These are always allowed regardless of user consent
};

// Get all cookies for debugging
export const getAllCookies = () => {
  const cookies = {};
  document.cookie.split(';').forEach(cookie => {
    const [name, value] = cookie.trim().split('=');
    if (name && value) {
      cookies[name] = value;
    }
  });
  return cookies;
};

// Export cookie status for admin dashboard
export const getCookieStatus = () => {
  const consent = getCookieConsent();
  const summary = getCookieConsentSummary();
  
  return {
    hasConsent: !!consent,
    isExpired: isCookieConsentExpired(),
    summary,
    currentCookies: getAllCookies(),
    allowedTypes: Object.keys(COOKIE_TYPES).filter(key => 
      isCookieAllowed(COOKIE_TYPES[key])
    )
  };
};